// Command Palette with Category Filters
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, X, ChevronRight, Star, Clock, Grid, 
  Filter, Check, Sparkles, BarChart2 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: any[];
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ 
  isOpen, 
  onClose, 
  menuItems 
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [recentItems, setRecentItems] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');

  // Kategorie z emoji i kolorami
  const categories = [
    { id: 'all', label: 'Wszystkie', emoji: '🎯', color: 'from-violet-500 to-purple-500', count: 0 },
    { id: 'command-center', label: 'Dowodzenie', emoji: '🎯', color: 'from-violet-500 to-indigo-500', count: 0 },
    { id: 'sales-center', label: 'Sprzedaż', emoji: '💰', color: 'from-emerald-500 to-green-500', count: 0 },
    { id: 'engineering-center', label: 'Inżynieria', emoji: '⚙️', color: 'from-orange-500 to-red-500', count: 0 },
    { id: 'design-studio', label: 'Projektowanie', emoji: '🎨', color: 'from-pink-500 to-rose-500', count: 0 },
    { id: 'ai-center', label: 'AI', emoji: '🤖', color: 'from-purple-500 to-violet-500', count: 0 },
    { id: 'business-center', label: 'Biznes', emoji: '📊', color: 'from-cyan-500 to-blue-500', count: 0 },
    { id: 'knowledge-center', label: 'Wiedza', emoji: '📚', color: 'from-indigo-500 to-blue-500', count: 0 },
  ];

  // Grupowanie modułów po kategoriach
  const groupedItems = useMemo(() => {
    const groups: Record<string, any[]> = {};
    
    menuItems.forEach(item => {
      const categoryId = item.categoryId || 'other';
      if (!groups[categoryId]) {
        groups[categoryId] = [];
      }
      groups[categoryId].push(item);
    });

    // Aktualizuj liczniki
    categories.forEach(cat => {
      if (cat.id !== 'all') {
        cat.count = groups[cat.id]?.length || 0;
      } else {
        cat.count = menuItems.length;
      }
    });

    return groups;
  }, [menuItems]);

  // Filtrowane elementy
  const filteredItems = useMemo(() => {
    let items = selectedCategory === 'all' 
      ? menuItems 
      : groupedItems[selectedCategory] || [];

    if (search) {
      items = items.filter(item => 
        item.label.toLowerCase().includes(search.toLowerCase()) ||
        item.keywords?.some((k: string) => k.toLowerCase().includes(search.toLowerCase())) ||
        item.description?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Sortuj po priorytecie i ostatnio używanych
    return items.sort((a, b) => {
      const aRecent = recentItems.indexOf(a.id);
      const bRecent = recentItems.indexOf(b.id);
      
      if (aRecent !== -1 && bRecent !== -1) {
        return aRecent - bRecent;
      }
      if (aRecent !== -1) return -1;
      if (bRecent !== -1) return 1;
      
      return (a.priority || 999) - (b.priority || 999);
    });
  }, [search, selectedCategory, menuItems, groupedItems, recentItems]);

  // Load recent items from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('plexisystem_recent_modules');
    if (saved) {
      setRecentItems(JSON.parse(saved));
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredItems.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : filteredItems.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            handleSelect(filteredItems[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        // Shortcuts for categories (1-8)
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
          const catIndex = parseInt(e.key) - 1;
          if (categories[catIndex]) {
            setSelectedCategory(categories[catIndex].id);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredItems, selectedIndex]);

  // Handle selection
  const handleSelect = (item: any) => {
    // Update recent items
    const newRecent = [item.id, ...recentItems.filter(id => id !== item.id)].slice(0, 5);
    setRecentItems(newRecent);
    localStorage.setItem('plexisystem_recent_modules', JSON.stringify(newRecent));
    
    navigate(item.href);
    onClose();
    setSearch('');
    setSelectedIndex(0);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[10vh] px-4">
        <div className="w-full max-w-4xl bg-black/90 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Szukaj modułu... (wpisz nazwę lub użyj / dla komend)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelectedIndex(0);
                }}
                className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-lg"
                autoFocus
              />
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <button
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all"
                  title={viewMode === 'grid' ? 'Widok listy' : 'Widok siatki'}
                >
                  <Grid className={`w-4 h-4 ${viewMode === 'grid' ? 'text-violet-400' : 'text-gray-400'}`} />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
              {categories.map((cat, index) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r ' + cat.color + ' text-white'
                      : 'bg-white/5 hover:bg-white/10 text-gray-400'
                  }`}
                >
                  <span className="text-sm">{cat.emoji}</span>
                  <span className="text-sm font-medium">{cat.label}</span>
                  <span className="text-xs opacity-70">({cat.count})</span>
                  {index < 8 && (
                    <kbd className="text-xs bg-black/30 px-1 rounded">
                      {index + 1}
                    </kbd>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[60vh] overflow-y-auto p-4">
            {filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">
                  Nie znaleziono modułów dla "{search}"
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Spróbuj zmienić kategorię lub wyszukiwane słowo
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {filteredItems.map((item, index) => {
                  const Icon = item.icon;
                  const isSelected = index === selectedIndex;
                  const isRecent = recentItems.includes(item.id);
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`group relative p-4 rounded-xl transition-all text-left ${
                        isSelected
                          ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/50'
                          : 'bg-white/5 hover:bg-white/10 border border-white/10'
                      }`}
                    >
                      {/* Recent indicator */}
                      {isRecent && (
                        <div className="absolute top-2 right-2">
                          <Clock className="w-3 h-3 text-yellow-400" />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg bg-gradient-to-br ${
                          item.color || 'from-gray-500/20 to-gray-600/20'
                        }`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        {item.badge && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${item.badge.gradient} text-white font-bold`}>
                            {item.badge.text}
                          </span>
                        )}
                      </div>
                      
                      <h3 className="font-medium text-white mb-1 line-clamp-1">
                        {item.label}
                      </h3>
                      <p className="text-xs text-gray-400 line-clamp-2">
                        {item.description || item.category}
                      </p>
                      
                      {isSelected && (
                        <div className="absolute bottom-2 right-2">
                          <span className="text-xs text-gray-500">Enter ↵</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              // List View
              <div className="space-y-1">
                {filteredItems.map((item, index) => {
                  const Icon = item.icon;
                  const isSelected = index === selectedIndex;
                  const isRecent = recentItems.includes(item.id);
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isSelected
                          ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/50'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${
                        item.color || 'from-gray-500/20 to-gray-600/20'
                      }`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{item.label}</span>
                          {item.badge && (
                            <span className={`px-2 py-0.5 text-xs rounded-full ${item.badge.gradient} text-white font-bold`}>
                              {item.badge.text}
                            </span>
                          )}
                          {isRecent && (
                            <Clock className="w-3 h-3 text-yellow-400" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{item.categoryEmoji} {item.category}</span>
                          {item.description && (
                            <>
                              <span className="text-gray-600">•</span>
                              <span className="text-xs text-gray-400">{item.description}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/10 rounded">↑↓</kbd>
                nawiguj
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/10 rounded">↵</kbd>
                wybierz
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/10 rounded">1-8</kbd>
                kategorie
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white/10 rounded">ESC</kbd>
                zamknij
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {filteredItems.length} modułów
            </div>
          </div>
        </div>
      </div>
    </>
  );
};